import { RmsAccountOptions } from '@/backend/features/badging/badging-configuration/types';
import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { AccountBadgeData } from '@juvare/badging/backend/features/badges/shared';
import { BarcodeAamvaData } from '@juvare/badging/backend/features/barcodesAamva/types/BarcodeAamvaData';
import {
    generateAamvaDataForAccountBadge,
    GenerateAamvaDataForAccountBadgeInputData,
} from '@juvare/badging/backend/features/barcodesAamvaBadge/utils/generateAamvaDataForAccountBadge';
import { occupationEntityName } from '@juvare/credentialing/backend/config/classifiers/entities/entityTypes';
import { OccupationColumns, OccupationData } from '@juvare/credentialing/backend/config/shared';
import { AppContext } from '@juvare/platform-app/App';
import { JurisdictionData } from '@juvare/platform-app/Integration';
import { JurisdictionColumns, jurisdictionEntityName } from '@juvare/platform-sdk-react/backend/features/default-classifiers/shared';
import { AppState } from '@juvare/platform-sdk-react/types';

export interface GetAamvaDataForBarcodeGenerationProps {
    context: AppContext<AppState>;
    accountBadgeData: AccountBadgeData;
    accountColumns: ExtendedAccountColumns;
}

/**
 * Map to AAMVA format for PDF417 badge generation
 */
export const getAamvaDataForBarcodeGeneration = ({
    context,
    accountBadgeData,
    accountColumns,
}: GetAamvaDataForBarcodeGenerationProps): BarcodeAamvaData => {
    const storage = context.storage<JurisdictionData, JurisdictionColumns>(jurisdictionEntityName);

    const jurisdictionListResponse = storage.list({
        filter: [{ column: 'postalCode', op: '=', value: context.appState.settings.esarVhp.clientCode }],
        limit: 1,
    });

    if (jurisdictionListResponse.total !== 1) {
        throw new Error('State not found');
    }

    const stateFullName = jurisdictionListResponse.rows[0].columns.name;

    return getAamvaDataForBarcodeGenerationInternal(context, accountBadgeData, accountColumns, {
        stateId: context.appState.settings.esarVhp.clientCode,
        stateFullName,
        issuanceNo: (accountBadgeData.reissueHistory?.length ?? 0) + 1,
        badgeIssuerId: 1,
        badgeVersion: 'B',
    });
};

export function getAamvaDataForBarcodeGenerationInternal(
    context: AppContext<AppState>,
    accountBadgeData: AccountBadgeData,
    account: ExtendedAccountColumns,
    options: GenerateAamvaDataForAccountBadgeInputData,
) {
    const result = generateAamvaDataForAccountBadge(context, accountBadgeData, account, options);

    // customize AAMVA data
    const accountOptions = accountBadgeData.options as RmsAccountOptions;
    if (accountOptions.occupationId) {
        const occupationStorage = context.storage<OccupationData, OccupationColumns>(occupationEntityName);
        const occupation = occupationStorage.get(accountOptions.occupationId);
        result.zctOccupationTitle = occupation?.data.name ?? '';
    }

    return result;
}
